// src/reporting/pdf/addStatistics.js

import { PDF_CONTENT_GAP, addSectionTitle, ensurePageSpace } from "./pdfLayout";

const STAT_FONT_SIZE = 9;
const STAT_LINE_HEIGHT = 11;
const STAT_ROW_GAP = 4;

function splitRowText(doc, row, width) {
  if (row.note) {
    return {
      noteLines: doc.splitTextToSize(String(row.note), width),
      labelLines: [],
      valueLines: [],
    };
  }

  return {
    noteLines: [],
    labelLines: doc.splitTextToSize(
      String(row.label || ""),
      width * 0.58
    ),
    valueLines: doc.splitTextToSize(
      String(row.value ?? "—"),
      width * 0.38
    ),
  };
}

function statisticsSectionHeight(doc, { rows = [] }, width, minimumHeight) {
  const rowsHeight = rows.reduce((height, row) => {
    const { noteLines, labelLines, valueLines } = splitRowText(doc, row, width);
    const lineCount = row.note
      ? Math.max(1, noteLines.length)
      : Math.max(1, labelLines.length, valueLines.length);
    return height + Math.max(15, lineCount * STAT_LINE_HEIGHT) + STAT_ROW_GAP;
  }, 0);

  return Math.max(minimumHeight, 34 + rowsHeight);
}

function drawStatisticsSection(ctx, { title, rows = [] }, x, y, width) {
  const { doc } = ctx;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(20);

  doc.text(title || "", x, y + 14);

  let yy = y + 34;

  for (const row of rows) {
    const { noteLines, labelLines, valueLines } = splitRowText(doc, row, width);
    const lineCount = row.note
      ? Math.max(1, noteLines.length)
      : Math.max(1, labelLines.length, valueLines.length);

    doc.setFont(
      "helvetica",
      row.note ? "italic" : row.bold ? "bold" : "normal"
    );
    doc.setFontSize(STAT_FONT_SIZE);
    doc.setTextColor(20);

    if (row.note) {
      doc.text(noteLines, x, yy);
    } else {
      doc.text(labelLines, x, yy);
      doc.text(valueLines, x + width, yy, { align: "right" });
    }

    yy += Math.max(15, lineCount * STAT_LINE_HEIGHT) + STAT_ROW_GAP;
  }
}

export function addStatistics(ctx, config = {}, startY) {
  const {
    title = "Statistics",
    sections = [],
    columns = 2,
    sectionHeight = 100,
    columnGap = 36,
    rowGap = 14,
  } = config;

  if (!Array.isArray(sections) || sections.length === 0) {
    return startY;
  }

  const { doc, margins, usableWidth } = ctx;

  const boxPaddingX = 10;
  const boxPaddingTop = 8;
  const boxPaddingBottom = 10;

  const innerLeft = margins.left + boxPaddingX;
  const innerWidth = usableWidth - boxPaddingX * 2;

  const colWidth =
    (innerWidth - columnGap * (columns - 1)) / columns;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(STAT_FONT_SIZE);

  const rowCount = Math.ceil(sections.length / columns);
  const sectionHeights = sections.map((section) =>
    statisticsSectionHeight(doc, section, colWidth, sectionHeight)
  );
  const gridRowHeights = Array.from({ length: rowCount }, (_, row) =>
    Math.max(
      ...sectionHeights.filter((_, index) => Math.floor(index / columns) === row)
    )
  );
  const gridRowOffsets = gridRowHeights.map((_, row) =>
    gridRowHeights
      .slice(0, row)
      .reduce((sum, height) => sum + height + rowGap, 0)
  );
  const contentHeight =
    gridRowHeights.reduce((sum, height) => sum + height, 0) +
    Math.max(0, rowCount - 1) * rowGap;

  const neededHeight =
    20 +
    contentHeight +
    boxPaddingTop +
    boxPaddingBottom +
    18;

  let y = ensurePageSpace(ctx, startY, neededHeight);

  y = addSectionTitle(ctx, title, y);

  const boxTop = y;

  const boxHeight =
    contentHeight +
    boxPaddingTop +
    boxPaddingBottom;

  // rounded borders
  doc.setDrawColor(220);
  doc.setLineWidth(0.8);
  doc.setFillColor(248, 248, 248);

  doc.roundedRect(
    margins.left,
    boxTop,
    usableWidth,
    boxHeight,
    6,
    6,
    "FD"
  );

  sections.forEach((section, index) => {
    const col = index % columns;
    const row = Math.floor(index / columns);

    const x =
      innerLeft +
      col * (colWidth + columnGap);

    const yy =
      y + boxPaddingTop + gridRowOffsets[row];

    drawStatisticsSection(
      ctx,
      section,
      x,
      yy,
      colWidth
    );
  });

  return boxTop + boxHeight + PDF_CONTENT_GAP;
}
